import React, { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import TaskEdit from 'components/Task/TaskEdit';
import { FormRef } from '@lib/components/ReactHookForm/types';
import BreadCrumbs from '@lib/components/BreadCrumbs/BreadCrumbs';
import { getBreadCrumbsDataByKey } from 'utils/breadcrumbs';
import useSearchListQueries from 'routes/Task/utils/useSearchListQueries';
import getTaskFields from 'routes/Task/utils/useFields/getTaskFields';
import useModalManagement from '@lib/hooks/useModalManagement';
import DocumentsModalTableMultiselect from 'components/DocumentsModalTableMultiselect/DocumentsModalTableMultiselect';
import { Option } from '@lib/interfaces/form';
import { AccessComponentEnum, AccessLevelEnum } from 'graphql-common';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import _get from 'lodash/get';
import { FieldNames } from './enums';
import useActions from './utils/useActions';
import useDocumentationLinksPrefill from './utils/useDocumentationLinksPrefill';

const SELECT_DOCUMENTS_MODAL = 'select-documents';

export default function TaskEditContainer() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const formRef = useRef<FormRef>(undefined);

  const { getUserAccessByComponent, getUserAccessByCompany }: ContextProps =
    useOutletContext();
  const isDocumentationLocked =
    getUserAccessByCompany(AccessComponentEnum.DocumentationManagement) ===
      AccessLevelEnum.LimitedRead ||
    getUserAccessByComponent(AccessComponentEnum.DocumentationManagement) ===
      AccessLevelEnum.LimitedRead;

  const {
    getApproversForEditFormLoadOptions: getApproversLoadOptions,
    getAssetsLoadOptions,
    getDocumentationsLoadOptions,
    getSiteAreasLoadOptions,
    getSitesLoadOptions,
    getTemplateVersionsLoadOptions,
    getTemplatesLoadOptions,
  } = useSearchListQueries();

  // Documents modal
  const useModalManagementResult = useModalManagement([SELECT_DOCUMENTS_MODAL]);

  const openDocumentsModalTableMultiselect = useCallback(() => {
    useModalManagementResult.openModal(SELECT_DOCUMENTS_MODAL);
  }, [useModalManagementResult]);

  const onCloseDocumentsModal = useCallback(() => {
    useModalManagementResult.closeModal(SELECT_DOCUMENTS_MODAL);
  }, [useModalManagementResult]);

  const onDocumentsSelect = useCallback((values: string[]) => {
    if (formRef.current) {
      formRef.current.setValue(
        FieldNames.documentationLinks,
        values.length ? values.map((docId) => ({ value: docId })) : [],
        { shouldDirty: true, shouldTouch: true, shouldValidate: true },
      );
    }
  }, []);

  const isDocumentsModalOpen = useModalManagementResult.isModalOpened(
    SELECT_DOCUMENTS_MODAL,
  );

  const initialDocumentationLinksRef = useRef<Option[]>([]);

  const {
    onUpdate,
    onViewFormTemplate,
    taskData,
    taskError,
    taskLoading,
    updateMutationError,
    updateTaskLoading,
  } = useActions({
    formRef,
    initialDocumentationLinksRef,
    isDocumentationLocked,
  });

  const { loading: documentationLinksPrefillLoading } =
    useDocumentationLinksPrefill({
      formRef,
      groupKey: taskData?.groupKey,
      initialDocumentationLinksRef,
      skip: isDocumentationLocked,
    });

  const canFormTemplateUpdate =
    _get(taskData, 'formTemplate.canUpdate.value') || false;
  const canQrCodeRelatedInfoUpdate =
    _get(taskData, 'canQrCodeRelatedInfoUpdate.value') || false;
  const formFields = getTaskFields({
    getApproversLoadOptions,
    getAssetsLoadOptions,
    getDocumentationsLoadOptions,
    getSiteAreasLoadOptions,
    getSitesLoadOptions,
    canQrCodeRelatedInfoUpdate,
    getTemplateVersionsLoadOptions: canFormTemplateUpdate
      ? getTemplateVersionsLoadOptions
      : undefined,
    getTemplatesLoadOptions: canFormTemplateUpdate
      ? getTemplatesLoadOptions
      : undefined,
    isDocumentationLocked,
    onViewFormTemplate,
    openDocumentsModalTableMultiselect,
    t,
    isCreate: false,
  });

  const breadCrumbsData = getBreadCrumbsDataByKey({
    pathname,
    t,
    entity: { name: taskData?.name, id: taskData?.id },
  });

  return (
    <>
      <BreadCrumbs data={breadCrumbsData} />
      <TaskEdit
        updateMutationError={updateMutationError}
        updateTaskLoading={updateTaskLoading}
        formFields={formFields}
        formRef={formRef}
        onSubmit={onUpdate}
        taskData={taskData}
        taskError={taskError}
        taskLoading={taskLoading || documentationLinksPrefillLoading}
      />
      <DocumentsModalTableMultiselect
        isOpen={isDocumentsModalOpen}
        onClose={onCloseDocumentsModal}
        onSelect={onDocumentsSelect}
        formRef={formRef}
        fieldName={FieldNames.documentationLinks}
      />
    </>
  );
}
